// src/sectors/pilates/members/components/paymentReceive/PlanPickerStep.jsx
import React, { useEffect, useMemo, useState } from 'react';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Icon from '@mui/material/Icon';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import { alpha } from '@mui/material/styles';

import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDInput from 'components/MDInput';

import { membersApi } from 'sectors/pilates/members/api/membersApi';
import { ToggleButton, ToggleButtonGroup } from '@mui/material';

// ---------------- helpers ----------------
const MONTHS_TR = [
  'Ocak',
  'Şubat',
  'Mart',
  'Nisan',
  'Mayıs',
  'Haziran',
  'Temmuz',
  'Ağustos',
  'Eylül',
  'Ekim',
  'Kasım',
  'Aralık',
];

function parseDate(d) {
  if (!d) return null;
  const dt = d instanceof Date ? d : new Date(d);
  return Number.isNaN(dt.getTime()) ? null : dt;
}
function addMonths(date, months) {
  const d = new Date(date.getTime());
  const day = d.getDate();
  d.setMonth(d.getMonth() + months);
  if (d.getDate() !== day) d.setDate(0);
  return d;
}
function dMonYYYY_TR(dateLike) {
  const d = parseDate(dateLike);
  if (!d) return '—';
  return `${d.getDate()} ${MONTHS_TR[d.getMonth()]} ${d.getFullYear()}`;
}
function parseLocalDateString(s) {
  if (!s || typeof s !== 'string') return null;
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const da = Number(m[3]);
  const dt = new Date(y, mo - 1, da);
  return Number.isNaN(dt.getTime()) ? null : dt;
}
function moneyTRY(n) {
  if (typeof n !== 'number' || !Number.isFinite(n)) return '—';
  return new Intl.NumberFormat('tr-TR').format(n) + ' ₺';
}
function tryFromInput(x) {
  if (x === '' || x == null) return 0;
  const s = String(x).trim().replace(/\./g, '').replace(',', '.');
  const n = Number(s);
  if (!Number.isFinite(n)) return 0;
  return Math.round(n);
}
function todayLocalDateStr() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const da = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${da}`;
}
// ---------------- component ----------------
export function PlanPickerStep({
  theme,
  styles,
  memberId,
  selectedPlanId,
  onSelectPlanId,

  // ✅ NEW: modal’a “hazır taslak” akıt
  onDraftChange, // (draft) => void
}) {
  const { accent, chipSx } = styles;

  const [loadingList, setLoadingList] = useState(false);
  const [err, setErr] = useState('');
  const [packages, setPackages] = useState([]);

  const [payTry, setPayTry] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('CASH'); // CASH | CARD | TRANSFER
  const [note, setNote] = useState('');
  const [paidDate, setPaidDate] = useState(todayLocalDateStr()); // ✅ EKLE
  const [earlyMode, setEarlyMode] = useState(false);
  const [transferNext, setTransferNext] = useState(true);

  // fetch packages
  useEffect(() => {
    if (!memberId) return;

    let alive = true;
    setLoadingList(true);
    setErr('');

    membersApi
      .memberPackages(memberId)
      .then((res) => {
        if (!alive) return;

        const list = Array.isArray(res)
          ? res
          : Array.isArray(res?.items)
          ? res.items
          : Array.isArray(res?.content)
          ? res.content
          : Array.isArray(res?.data)
          ? res.data
          : Array.isArray(res?.packages)
          ? res.packages
          : [];

        setPackages(Array.isArray(list) ? list : []);
      })
      .catch((e) => {
        if (!alive) return;
        setErr(e?.message || 'Paketler alınamadı');
        setPackages([]);
      })
      .finally(() => {
        if (!alive) return;
        setLoadingList(false);
      });

    return () => {
      alive = false;
    };
  }, [memberId]);

  const rows = useMemo(() => {
    return (packages || []).map((p) => {
      const id = p?.id ?? p?._id ?? p?.packageId ?? p?.memberPackageId;
      const planName = p?.planName ?? p?.planSnapshot?.name ?? p?.name ?? 'Paket';

      const planPriceTry = typeof p?.planPriceTry === 'number' ? p.planPriceTry : null;
      const agreedPriceTry =
        typeof p?.agreedPriceTry === 'number' ? p.agreedPriceTry : planPriceTry;
      const totalTry =
        typeof p?.totalTry === 'number' ? p.totalTry : agreedPriceTry ?? planPriceTry ?? 0;
      const paidTry = typeof p?.paidTry === 'number' ? p.paidTry : 0;
      const debtTry =
        typeof p?.debtTry === 'number' ? p.debtTry : Math.max(0, (totalTry || 0) - (paidTry || 0));

      const startDt = parseLocalDateString(p?.startAt);
      const endDtFromApi = parseLocalDateString(p?.endAt);

      const durationMonths =
        typeof p?.durationMonths === 'number' && Number.isFinite(p.durationMonths)
          ? p.durationMonths
          : 1;

      const estimatedEndDt = endDtFromApi
        ? endDtFromApi
        : startDt
        ? addMonths(startDt, durationMonths)
        : null;

      return {
        raw: p,
        id,
        planName,

        startAtLabel: startDt ? dMonYYYY_TR(startDt) : 'Başlamadı',
        endAtLabel: estimatedEndDt ? dMonYYYY_TR(estimatedEndDt) : '—',

        planPriceTry,
        agreedPriceTry,
        totalTry,
        paidTry,
        debtTry,
        hasDebt: debtTry > 0,

        durationMonths,
      };
    });
  }, [packages]);

  const anyDebt = rows.some((r) => r.hasDebt);
  const selectedRow = rows.find((r) => String(r.id) === String(selectedPlanId));

  // seçili row değişince payTry önerisi
  useEffect(() => {
    if (!selectedRow) {
      setPayTry('');
      return;
    }

    if (selectedRow.hasDebt) {
      setPayTry(String(selectedRow.debtTry ?? 0));
    } else if (!anyDebt && earlyMode) {
      const def =
        selectedRow.totalTry ?? selectedRow.agreedPriceTry ?? selectedRow.planPriceTry ?? 0;
      setPayTry(String(def));
    } else {
      setPayTry('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPlanId, earlyMode]);
  useEffect(() => {
    setPaidDate(todayLocalDateStr());
  }, [selectedPlanId]);
  // borç varsa earlyMode kapanır
  useEffect(() => {
    if (anyDebt) {
      setEarlyMode(false);
      setTransferNext(true);
    }
  }, [anyDebt]);

  // ✅ draft’i modal’a akıt
  useEffect(() => {
    const amountTry = tryFromInput(payTry);

    // draft yok
    if (!selectedRow) {
      onDraftChange?.(null);
      return;
    }

    // borç varsa: PLAN payment (bu “tahsilat”)
    if (anyDebt) {
      if (!selectedRow.hasDebt || amountTry <= 0) {
        onDraftChange?.(null);
        return;
      }
      onDraftChange?.({
        kind: 'PLAN_DEBT',
        memberPlanId: selectedRow.id,
        planName: selectedRow.planName,
        startAtLabel: selectedRow.startAtLabel,
        endAtLabel: selectedRow.endAtLabel,
        amountTry,
        maxAmountTry: selectedRow.debtTry || 0,
        paymentMethod,
        paidDate: paidDate || todayLocalDateStr(),
        note: note?.trim() ? note : 'Borç tahsilatı',
      });
      return;
    }

    // borç yok: earlyMode açık ise earlyTransfer draft
    if (!earlyMode || amountTry <= 0) {
      onDraftChange?.(null);
      return;
    }

    onDraftChange?.({
      kind: 'PLAN_EARLY',
      fromPlanId: selectedRow.id,
      planName: selectedRow.planName,
      startAtLabel: selectedRow.startAtLabel,
      endAtLabel: selectedRow.endAtLabel,
      renewPackage: !!transferNext,
      amountTry,
      paymentMethod,
      paidDate: paidDate || todayLocalDateStr(), // ✅ EKLE
      note: note?.trim() ? note : 'Erken ödeme',
    });
  }, [
    selectedRow,
    anyDebt,
    earlyMode,
    transferNext,
    payTry,
    paymentMethod,
    paidDate,
    note,
    onDraftChange,
  ]);
  const canSelectRow = (r) => {
    if (anyDebt) return r.hasDebt;
    return true;
  };

  const infoLine = (r) => {
    const total = r.totalTry ?? r.agreedPriceTry ?? r.planPriceTry ?? null;
    const paid = r.paidTry ?? 0;
    const remaining = r.debtTry ?? Math.max(0, (total || 0) - paid);

    const parts = [];
    if (r.planPriceTry != null) parts.push(`Liste: ${moneyTRY(r.planPriceTry)}`);
    if (r.agreedPriceTry != null) parts.push(`Anlaşılan: ${moneyTRY(r.agreedPriceTry)}`);
    parts.push(`Ödenen: ${moneyTRY(paid)}`);
    parts.push(`Kalan: ${moneyTRY(remaining)}`);
    return parts.join(' • ');
  };

  const primaryChip = (r) => {
    if (r.hasDebt) return { color: 'error', label: `Borç: ${moneyTRY(r.debtTry ?? 0)}` };
    if ((r.paidTry || 0) > 0) return { color: 'success', label: 'Borç yok' };
    return { color: 'info', label: 'Ödeme yok' };
  };

  const showPaymentBox =
    selectedRow && ((anyDebt && selectedRow.hasDebt) || (!anyDebt && earlyMode));

  const paymentBoxTitle = anyDebt ? 'Tahsil edilecek tutar' : 'Erken ödeme tutarı';
  const paymentBoxHint = anyDebt
    ? `Kalan borç: ${moneyTRY(selectedRow?.debtTry ?? 0)}`
    : `Önerilen: ${moneyTRY(
        selectedRow?.totalTry ?? selectedRow?.agreedPriceTry ?? selectedRow?.planPriceTry ?? 0
      )}`;

  if (!memberId) {
    return (
      <MDBox sx={{ p: 2 }}>
        <MDTypography fontWeight="bold">Üye seçilemedi</MDTypography>
        <MDTypography variant="caption" sx={{ color: 'text.secondary' }}>
          Üye id bulunamadığı için paket listesi alınamadı.
        </MDTypography>
      </MDBox>
    );
  }

  if (loadingList && rows.length === 0) {
    return (
      <MDBox sx={{ p: 2 }}>
        <MDTypography fontWeight="bold">Paketler yükleniyor…</MDTypography>
        <MDTypography variant="caption" sx={{ color: 'text.secondary' }}>
          Üyenin paketleri getiriliyor.
        </MDTypography>
      </MDBox>
    );
  }

  if (err && rows.length === 0) {
    return (
      <MDBox
        sx={{
          p: 2,
          borderRadius: '16px',
          border: `1px dashed ${alpha(theme.palette.divider, 0.9)}`,
          background: alpha(theme.palette.background.paper, 0.6),
        }}
      >
        <MDTypography fontWeight="bold">Paketler alınamadı</MDTypography>
        <MDTypography variant="caption" sx={{ color: 'text.secondary' }}>
          {err}
        </MDTypography>
      </MDBox>
    );
  }

  if (!rows.length) {
    return (
      <MDBox
        sx={{
          p: 2,
          borderRadius: '16px',
          border: `1px dashed ${alpha(theme.palette.divider, 0.9)}`,
          background: alpha(theme.palette.background.paper, 0.6),
        }}
      >
        <MDTypography fontWeight="bold">Paket bulunamadı</MDTypography>
        <MDTypography variant="caption" sx={{ color: 'text.secondary' }}>
          Üyenin paketi yok veya liste boş.
        </MDTypography>
      </MDBox>
    );
  }

  return (
    <MDBox sx={{ display: 'grid', gap: 1.25 }}>
      {!anyDebt ? (
        <MDBox
          sx={{
            p: 1.25,
            borderRadius: '16px',
            border: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
            background: alpha(theme.palette.background.paper, 0.55),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
          }}
        >
          <MDBox>
            <MDTypography fontWeight="bold">Erken ödeme</MDTypography>
            <MDTypography variant="caption" sx={{ color: 'text.secondary' }}>
              Borç yoksa sonraki dönem için ödeme alabilirsin
            </MDTypography>
          </MDBox>

          <FormControlLabel
            sx={{ m: 0 }}
            control={
              <Switch checked={earlyMode} onChange={(e) => setEarlyMode(e.target.checked)} />
            }
            label=""
          />
        </MDBox>
      ) : null}

      <List sx={{ p: 0, display: 'grid', gap: 1 }}>
        {rows.map((r) => {
          const selected = String(r.id) === String(selectedPlanId);
          const selectable = canSelectRow(r);
          const chip = primaryChip(r);

          const inlineShowBox = selected && ((anyDebt && r.hasDebt) || (!anyDebt && earlyMode));

          const boxTitle = anyDebt ? 'Tahsil edilecek tutar' : 'Erken ödeme tutarı';
          const boxHint = anyDebt
            ? `Kalan borç: ${moneyTRY(r.debtTry ?? 0)}`
            : `Önerilen: ${moneyTRY(r.totalTry ?? r.agreedPriceTry ?? r.planPriceTry ?? 0)}`;

          return (
            <MDBox key={String(r.id)} sx={{ display: 'grid', gap: 1 }}>
              <ListItemButton
                disabled={!selectable}
                onClick={() => {
                  if (!selectable) return;
                  onSelectPlanId?.(r.id);
                }}
                sx={{
                  opacity: selectable ? 1 : 0.55,
                  borderRadius: '16px',
                  border: `1px solid ${
                    selected ? alpha(accent, 0.55) : alpha(theme.palette.divider, 0.9)
                  }`,
                  background: selected ? alpha(accent, 0.12) : 'transparent',
                  transition: 'all 160ms ease',
                  '&:hover': selectable
                    ? { borderColor: alpha(accent, 0.45), background: alpha(accent, 0.08) }
                    : undefined,
                }}
              >
                <Icon sx={{ mr: 1, color: selected ? alpha(accent, 0.95) : 'text.secondary' }}>
                  {selected ? 'check_circle' : 'radio_button_unchecked'}
                </Icon>

                <ListItemText
                  primary={
                    <MDBox
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                      gap={1}
                    >
                      <MDBox sx={{ minWidth: 0 }}>
                        <MDTypography fontWeight="bold" noWrap>
                          {r.planName}
                        </MDTypography>

                        <MDTypography variant="caption" sx={{ color: 'text.secondary' }}>
                          Başlangıç: {r.startAtLabel} • Tahmini bitiş: {r.endAtLabel}
                        </MDTypography>

                        <MDTypography variant="caption" sx={{ color: 'text.secondary' }}>
                          {infoLine(r)}
                        </MDTypography>
                      </MDBox>

                      <Chip size="small" color={chip.color} label={chip.label} sx={chipSx} />
                    </MDBox>
                  }
                />
              </ListItemButton>

              {inlineShowBox ? (
                <MDBox
                  sx={{
                    p: 1.5,
                    borderRadius: '16px',
                    border: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
                    background: alpha(theme.palette.background.paper, 0.55),
                    display: 'grid',
                    gap: 1,
                  }}
                >
                  {!anyDebt && earlyMode ? (
                    <>
                      <MDBox
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        gap={1}
                      >
                        <MDBox>
                          <MDTypography fontWeight="bold">Paket yenilensin mi?</MDTypography>
                          <MDTypography variant="caption" sx={{ color: 'text.secondary' }}>
                            Evet ise yeni dönem paketi kaydedilir (startAt boş), ödeme ona bağlanır.
                          </MDTypography>
                        </MDBox>

                        <FormControlLabel
                          sx={{ m: 0 }}
                          control={
                            <Switch
                              checked={transferNext}
                              onChange={(e) => setTransferNext(e.target.checked)}
                            />
                          }
                          label=""
                        />
                      </MDBox>
                      <Divider />
                    </>
                  ) : null}

                  <MDBox>
                    <MDTypography fontWeight="bold">{boxTitle}</MDTypography>
                    <MDTypography variant="caption" sx={{ color: 'text.secondary' }}>
                      {boxHint}
                    </MDTypography>
                  </MDBox>
                  <MDBox
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 1,
                    }}
                  >
                    <MDBox>
                      <MDTypography fontWeight="bold">Ödeme tipi</MDTypography>
                      <MDTypography variant="caption" sx={{ color: 'text.secondary' }}>
                        Nakit / Kart / Havale
                      </MDTypography>
                    </MDBox>

                    <ToggleButtonGroup
                      value={paymentMethod}
                      exclusive
                      size="small"
                      onChange={(e, val) => {
                        if (val) setPaymentMethod(val);
                      }}
                      sx={{
                        height: 32,
                        bgcolor: 'grey.100',
                        borderRadius: 2,
                        '& .MuiToggleButton-root': {
                          px: 1.25,
                          fontSize: 11,
                          textTransform: 'none',
                          borderRadius: 2,
                        },
                      }}
                    >
                      <ToggleButton value="CASH">Nakit</ToggleButton>
                      <ToggleButton value="CARD">Kart</ToggleButton>
                      <ToggleButton value="TRANSFER">Havale</ToggleButton>
                    </ToggleButtonGroup>
                  </MDBox>

                  <MDBox sx={{ display: 'grid', gap: 1 }}>
                    <MDInput
                      label="Ödeme Tarihi"
                      type="date"
                      value={paidDate || ''}
                      onChange={(e) => {
                        console.log('paidDate change =>', e.target.value); // ✅ burada 2026-02-03 görmelisin
                        setPaidDate(e.target.value);
                      }}
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                    />

                    <MDInput
                      value={payTry}
                      onChange={(e) => setPayTry(e.target.value)}
                      placeholder="0"
                      fullWidth
                      inputProps={{ inputMode: 'decimal' }}
                    />
                  </MDBox>

                  {anyDebt ? (
                    <MDTypography variant="caption" sx={{ color: 'text.secondary' }}>
                      Not: Borç tahsilatında tutar kalan borçtan fazla olmamalı.
                    </MDTypography>
                  ) : null}
                </MDBox>
              ) : null}
            </MDBox>
          );
        })}
      </List>

      {anyDebt && !selectedRow ? (
        <MDTypography variant="caption" sx={{ color: 'text.secondary' }}>
          Borçlu bir paket seçerek tahsilatı başlatabilirsin.
        </MDTypography>
      ) : null}

      {!anyDebt && !earlyMode ? (
        <MDTypography variant="caption" sx={{ color: 'text.secondary' }}>
          Erken ödeme almak için “Erken ödeme”yi aç ve bir paket seç.
        </MDTypography>
      ) : null}
    </MDBox>
  );
}
